import { compact, getSlotCompoundVariant, memo, splitProps } from '../helpers.mjs';
import { createRecipe } from './create-recipe.mjs';

const badgeDefaultVariants = {
  "size": "md",
  "kind": "base",
  "variant": "default",
  "isRounded": false,
  "isPinned": false
}
const badgeCompoundVariants = [
  {
    "variant": "default",
    "kind": "base",
    "css": {
      "root": {
        "color": "text.high",
        "backgroundColor": "surface.neutral.medium"
      },
      "dot": {
        "backgroundColor": "surface.neutral.medium"
      }
    }
  },
  {
    "variant": "default",
    "kind": "subtle",
    "css": {
      "root": {
        "color": "text.high",
        "backgroundColor": "surface.neutral.low"
      },
      "dot": {
        "backgroundColor": "surface.neutral.low"
      }
    }
  },
  {
    "variant": "success",
    "kind": "base",
    "css": {
      "root": {
        "color": "text.success.inverted",
        "backgroundColor": "surface.success"
      },
      "dot": {
        "backgroundColor": "surface.success"
      }
    }
  },
  {
    "variant": "success",
    "kind": "subtle",
    "css": {
      "root": {
        "color": "text.success.high",
        "backgroundColor": "surface.success.low"
      },
      "dot": {
        "backgroundColor": "surface.success.low"
      }
    }
  },
  {
    "variant": "danger",
    "kind": "base",
    "css": {
      "root": {
        "color": "text.danger.inverted",
        "backgroundColor": "surface.danger"
      },
      "dot": {
        "backgroundColor": "surface.danger"
      }
    }
  },
  {
    "variant": "danger",
    "kind": "subtle",
    "css": {
      "root": {
        "color": "text.danger.high",
        "backgroundColor": "surface.danger.low"
      },
      "dot": {
        "backgroundColor": "surface.danger.low"
      }
    }
  },
  {
    "variant": "warning",
    "kind": "base",
    "css": {
      "root": {
        "color": "text.warning.inverted",
        "backgroundColor": "surface.warning"
      },
      "dot": {
        "backgroundColor": "surface.warning"
      }
    }
  },
  {
    "variant": "warning",
    "kind": "subtle",
    "css": {
      "root": {
        "color": "text.warning.high",
        "backgroundColor": "surface.warning.low"
      },
      "dot": {
        "backgroundColor": "surface.warning.low"
      }
    }
  },
  {
    "variant": "inverted",
    "kind": "base",
    "css": {
      "root": {
        "color": "text.inverted",
        "backgroundColor": "surface.neutral.high"
      },
      "dot": {
        "backgroundColor": "surface.neutral.high"
      }
    }
  },
  {
    "variant": "inverted",
    "kind": "subtle",
    "css": {
      "root": {
        "color": "text.high",
        "backgroundColor": "surface.neutral.medium"
      },
      "dot": {
        "backgroundColor": "surface.neutral.medium"
      }
    }
  },
  {
    "variant": "primary",
    "kind": "base",
    "css": {
      "root": {
        "color": "text.primary.inverted",
        "backgroundColor": "surface.primary"
      },
      "dot": {
        "backgroundColor": "surface.primary"
      }
    }
  },
  {
    "variant": "primary",
    "kind": "subtle",
    "css": {
      "root": {
        "color": "text.primary",
        "backgroundColor": "surface.primary.low"
      },
      "dot": {
        "backgroundColor": "surface.primary.low"
      }
    }
  },
  {
    "variant": "secondary",
    "kind": "base",
    "css": {
      "root": {
        "color": "text.secondary.inverted",
        "backgroundColor": "surface.secondary"
      },
      "dot": {
        "backgroundColor": "surface.secondary"
      }
    }
  },
  {
    "variant": "secondary",
    "kind": "subtle",
    "css": {
      "root": {
        "color": "text.secondary.high",
        "backgroundColor": "surface.secondary.low"
      },
      "dot": {
        "backgroundColor": "surface.secondary.low"
      }
    }
  },
  {
    "isPinned": true,
    "size": "sm",
    "css": {
      "root": {
        "height": "16px",
        "boxShadow": "2"
      }
    }
  },
  {
    "isPinned": false,
    "size": "sm",
    "css": {
      "root": {
        "height": "16px",
        "boxShadow": "none"
      }
    }
  },
  {
    "isPinned": true,
    "size": "md",
    "css": {
      "root": {
        "height": "16px",
        "boxShadow": "2"
      }
    }
  },
  {
    "isPinned": false,
    "size": "md",
    "css": {
      "root": {
        "height": "24px",
        "boxShadow": "none"
      }
    }
  }
]

const badgeSlotNames = [
  [
    "root",
    "Badge__root"
  ],
  [
    "content",
    "Badge__content"
  ],
  [
    "label",
    "Badge__label"
  ],
  [
    "icon",
    "Badge__icon"
  ],
  [
    "dot",
    "Badge__dot"
  ]
]
const badgeSlotFns = /* @__PURE__ */ badgeSlotNames.map(([slotName, slotKey]) => [slotName, createRecipe(slotKey, badgeDefaultVariants, getSlotCompoundVariant(badgeCompoundVariants, slotName))])

const badgeFn = memo((props = {}) => {
  return Object.fromEntries(badgeSlotFns.map(([slotName, slotFn]) => [slotName, slotFn.recipeFn(props)]))
})

const badgeVariantKeys = [
  "size",
  "kind",
  "variant",
  "isRounded",
  "isPinned"
]
const getVariantProps = (variants) => ({ ...badgeDefaultVariants, ...compact(variants) })

export const badge = /* @__PURE__ */ Object.assign(badgeFn, {
  __recipe__: false,
  __name__: 'badge',
  raw: (props) => props,
  variantKeys: badgeVariantKeys,
  variantMap: {
  "size": [
    "sm",
    "md"
  ],
  "kind": [
    "base",
    "subtle"
  ],
  "variant": [
    "default",
    "success",
    "danger",
    "warning",
    "inverted",
    "primary",
    "secondary",
    "mastering",
    "samples",
    "plugins",
    "collaboration",
    "extras",
    "courses",
    "distribution",
    "synchroArts"
  ],
  "isRounded": [
    "true"
  ],
  "isPinned": [
    "true",
    "false"
  ]
},
  splitVariantProps(props) {
    return splitProps(props, badgeVariantKeys)
  },
  getVariantProps
})